"use client";

import React, { useEffect, useState } from "react";

const getStatusColor = (status) => {
  const colors = {
    pending: "bg-yellow-100 text-yellow-700",
    selesai: "bg-green-100 text-green-700",
    batal: "bg-red-100 text-red-700",
  };
  return colors[status?.toLowerCase()] || "bg-slate-100 text-slate-700";
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Deteksi tipe transaksi dari field type & transaction_type
const matchFilter = (t, filterType) => {
  if (filterType === "all") return true;
 
  const type = (t.type || "").toLowerCase();
  const txType = (t.transaction_type || "").toLowerCase();
 
  if (filterType === "Simpanan Wajib") {
    return type === "simpanan wajib" || type.endsWith("- wajib") || type.endsWith(" wajib");
  }
  if (filterType === "Simpanan Pokok") {
    return type === "simpanan pokok" || type.endsWith("- pokok") || type.endsWith(" pokok");
  }
  if (filterType === "Simpanan Sukarela") {
    return type === "simpanan sukarela" || type.endsWith("- sukarela") || type.endsWith(" sukarela");
  }
  if (filterType === "Angsuran") {
    return type.includes("angsuran") || txType === "loan_payment";
  }
  return false;
};

export default function Transactions({ api }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  // Reset page saat filter berubah
  useEffect(() => {
    setCurrentPage(1);
  }, [filterType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get("/transaction");
      setData(res.data.recent_transactions || []);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Gagal mengambil data transaksi.");
    } finally {
      setLoading(false);
    }
  };

  // Filter data berdasarkan tipe
  const filteredData = data.filter((t) => matchFilter(t, filterType));

  // Pagination calculation
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-800">Daftar Transaksi</h1>

        <button
          onClick={fetchData}
          className="px-3 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* FILTER */}
      <div className="flex items-center gap-2 mb-3">
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="border rounded-xl px-3 py-2 text-sm text-slate-700"
        >
          <option value="all">Semua</option>
          <option value="Simpanan Wajib">Simpanan Wajib</option>
          <option value="Simpanan Pokok">Simpanan Pokok</option>
          <option value="Simpanan Sukarela">Simpanan Sukarela</option>
          <option value="Angsuran">Angsuran Pinjaman</option>
        </select>
      </div>

      {/* TABLE */}
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left font-semibold px-4 py-3 text-slate-600">
                Tanggal
              </th>
              <th className="text-left font-semibold px-4 py-3 text-slate-600">
                Deskripsi
              </th>
              <th className="text-left font-semibold px-4 py-3 text-slate-600">
                Tipe
              </th>
              <th className="text-left font-semibold px-4 py-3 text-slate-600">
                Jumlah
              </th>
              <th className="text-left font-semibold px-4 py-3 text-slate-600">
                Status
              </th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Memuat data...
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-red-500">
                  {error}
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-slate-400">
                  Tidak ada data transaksi
                </td>
              </tr>
            ) : (
              paginatedData.map((t, i) => (
                <tr key={i} className="border-t hover:bg-slate-50">
                  <td className="px-4 py-3 text-slate-700">
                    {formatDate(t.date)}
                  </td>

                  <td className="px-4 py-3 text-slate-800">
                    <div className="font-medium">{t.description}</div>
                  </td>

                  <td className="px-4 py-3 text-slate-500">{t.type}</td>

                  <td className="px-4 py-3 font-semibold">
                    <span
                      className={
                        t.amount_formatted?.startsWith("+")
                          ? "text-green-600"
                          : "text-red-600"
                      }
                    >
                      {t.amount_formatted}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded-lg text-xs ${getStatusColor(
                        t.status
                      )}`}
                    >
                      {t.status}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* PAGINATION */}
        {!loading && !error && filteredData.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 text-sm border-t bg-slate-50">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <span className="text-slate-600">
              Page <strong>{currentPage}</strong> dari <strong>{totalPages}</strong>
            </span>

            <button
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}